/**
 * The sign-in screen every Otii tool shares: Google Workspace, an emailed
 * link for people on other domains when the server offers it, plus the local
 * dev login when the server allows it. The app supplies its name and the
 * server's sign-in settings (GET /api/auth/config). An emailed link opens
 * SignInLinkScreen, which spends it only when the button is pressed.
 */
import { useQueryClient } from "@tanstack/react-query";
import { Mail, ShieldCheck } from "lucide-react";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router";
import wordmark from "../assets/otii-wordmark.png";
import { post } from "../lib/api";
import { Avatar } from "./avatar";
import { Button, Input, Loading, Notice } from "./ui";

export interface SignInConfig {
  /** True in an in-browser demo build: fictional data, pick who to be. */
  demo?: boolean;
  google: boolean;
  /** People on other domains can ask for an emailed sign-in link. */
  emailLink?: boolean;
  devLogin: boolean;
  allowedDomain: string;
  devUsers: { id: string; name: string; email: string; role: string }[];
}

export function SignInScreen({
  product,
  tagline,
  cfg,
  isLoading,
  roleLabel,
  demoNotice,
  noUsersHint,
}: {
  product: string;
  tagline: string;
  cfg: SignInConfig | undefined;
  isLoading: boolean;
  roleLabel: (role: string) => string;
  demoNotice?: { title: string; body: string };
  noUsersHint: string;
}) {
  const [params] = useSearchParams();
  const error = params.get("error");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [devError, setDevError] = useState<string | null>(null);

  const devLogin = async (userId: string) => {
    setBusy(userId);
    setDevError(null);
    try {
      await post("/api/auth/dev-login", { userId });
      await qc.invalidateQueries();
      navigate(params.get("next") ?? "/");
    } catch (e) {
      setDevError(e instanceof Error ? e.message : "Sign-in failed.");
      setBusy(null);
    }
  };

  return (
    <Frame product={product} tagline={tagline}>
          {isLoading || !cfg ? (
            <Loading />
          ) : (
            <div className="space-y-4">
              {error && <Notice tone="danger">{error}</Notice>}
              {cfg.demo ? (
                <Notice tone="info" title={demoNotice?.title}>
                  {demoNotice?.body}
                </Notice>
              ) : cfg.google ? (
                <a
                  href="/api/auth/google/start"
                  className="flex h-11 w-full items-center justify-center gap-3 rounded-lg border border-line bg-paper text-sm font-bold text-ink shadow-sm transition-colors hover:bg-cream"
                >
                  <GoogleMark />
                  Sign in with Google
                </a>
              ) : (
                <Notice tone="info" title="Google sign-in isn't set up yet">
                  Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to the server's environment. See the README.
                </Notice>
              )}
              {!cfg.demo && (
              <p className="text-center text-xs text-muted">
                <ShieldCheck className="mr-1 inline size-3.5 align-[-2px]" />
                {cfg.emailLink ? `Google for @${cfg.allowedDomain} accounts` : `Only @${cfg.allowedDomain} Google Workspace accounts can sign in.`}
              </p>
              )}
              {!cfg.demo && cfg.emailLink && <EmailLinkForm allowedDomain={cfg.allowedDomain} />}

              {cfg.devLogin && (
                <div className="border-t border-line-soft pt-4">
                  <div className="eyebrow mb-2">{cfg.demo ? "Choose who to be" : "Local dev login"}</div>
                  {!cfg.demo && <p className="mb-3 text-xs text-muted">Shown because AUTH_DEV_LOGIN is on. Never enable it on a shared server.</p>}
                  {devError && <Notice tone="danger" className="mb-2">{devError}</Notice>}
                  <div className="space-y-1.5">
                    {cfg.devUsers.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        disabled={!!busy}
                        onClick={() => devLogin(u.id)}
                        className="flex w-full items-center gap-3 rounded-lg border border-line-soft px-3 py-2 text-left transition-colors hover:border-sage-300 hover:bg-sage-50 disabled:opacity-60"
                      >
                        <Avatar name={u.name} size={30} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-ink">{u.name}</span>
                          <span className="block truncate text-xs text-muted">{u.email}</span>
                        </span>
                        <span className="text-[11px] font-bold text-terra-text uppercase">{roleLabel(u.role)}</span>
                      </button>
                    ))}
                    {cfg.devUsers.length === 0 && <p className="text-sm text-muted">{noUsersHint}</p>}
                  </div>
                </div>
              )}
            </div>
          )}
    </Frame>
  );
}

function Frame({ product, tagline, children }: { product: string; tagline: string; children: ReactNode }) {
  return (
    <div className="grid min-h-screen place-items-center bg-moss px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <img src={wordmark} alt="get otii" className="h-16 w-auto" />
          <h1 className="mt-4 text-3xl font-bold text-paper">{product}</h1>
          <p className="mt-1 text-sm text-paper/60">{tagline}</p>
        </div>
        <div className="rounded-2xl bg-paper p-6 shadow-[var(--shadow-pop)]">{children}</div>
      </div>
    </div>
  );
}

/** Other domains: ask for a link by email. The answer never says whether the address has access. */
function EmailLinkForm({ allowedDomain }: { allowedDomain: string }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const address = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) return setError("Use an email address.");
    if (address.endsWith(`@${allowedDomain.toLowerCase()}`)) return setError(`@${allowedDomain} accounts sign in with Google.`);
    setBusy(true);
    setError(null);
    try {
      await post("/api/auth/link", { email: address });
      setSent(address);
    } catch (err) {
      setError(err instanceof Error ? err.message : "That didn't work.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border-t border-line-soft pt-4">
      <div className="eyebrow mb-2">Other email addresses</div>
      {sent ? (
        <Notice tone="success" title="Link sent" action={<Button size="sm" variant="ghost" onClick={() => { setSent(null); setEmail(""); }}>Another address</Button>}>
          {sent} · works once, for 15 minutes
        </Notice>
      ) : (
        <form onSubmit={submit} className="space-y-2" noValidate>
          <Input type="email" autoComplete="email" aria-label="Email address" placeholder="name@company.com" value={email} invalid={!!error} onChange={(e) => { setEmail(e.target.value); setError(null); }} />
          {error && <p className="text-xs text-danger-ink">{error}</p>}
          <Button type="submit" className="w-full" icon={<Mail className="size-4" />} loading={busy}>
            Email me a sign-in link
          </Button>
        </form>
      )}
    </div>
  );
}

/** Where an emailed link lands (/sign-in/link#token): spent only when the button is pressed, so a mail scanner can't use it up. */
export function SignInLinkScreen({ product, tagline }: { product: string; tagline: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [token] = useState(() => (typeof window === "undefined" ? "" : window.location.hash.slice(1)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(token ? null : "That link isn't complete. Ask for a new one.");
  useEffect(() => {
    // the token stays out of the history
    if (token) window.history.replaceState(null, "", window.location.pathname);
  }, [token]);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await post("/api/auth/link/redeem", { token });
      await qc.invalidateQueries();
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed.");
      setBusy(false);
    }
  };
  return (
    <Frame product={product} tagline={tagline}>
      <div className="space-y-4">
        {error && <Notice tone="danger">{error}</Notice>}
        {token && !error && (
          <Button variant="primary" className="h-11! w-full" loading={busy} onClick={go}>
            Sign in
          </Button>
        )}
        {error && (
          <a href="/sign-in" className="block text-center text-sm font-semibold text-olive hover:underline">
            Sign-in page
          </a>
        )}
      </div>
    </Frame>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="size-5" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}
